import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';

import {
  API_IDS,
  ASSIGNMENT_END_REASON,
  QUESTION_RESPONSE_STATUS,
  QUESTION_TYPES,
  STUDENTASSIGNMENT_STATUS
} from '../../constants';
import { getAssignmentDetails, getAssignmentEndUrl, hasAttemptedQuestion } from '../../utils';
import {
  useBookmarkQuestion,
  useEndStudentAssignment,
  useUpdateStudentAssignment,
  useUploadFileStudentAssignment
} from '../../queries';
import { useParams } from '../useRouteParams';
import { useContextEffect } from '../useBetterHooks';
import { useDispatch } from 'react-redux';
import {
  incrementFailureCount,
  resetAllFailureCounts
} from '../../redux/reducers/api-call-state-reducer';
import { useCheckAssignmentAccess } from './index';

const useStudentAssignmentActions = ({ assignmentData, refetchAssignment }) => {
  const { questionId, studentAssignmentId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { mutate: updateStudentAssignment, mutateAsync: updateStudentAssignmentAsync, isLoading: isUpdateLoading } =
    useUpdateStudentAssignment(studentAssignmentId);
  const { mutateAsync: uploadStudentFileAsync, isLoading: isUploadLoading } =
    useUploadFileStudentAssignment(studentAssignmentId);
  const { mutate: bookmarkQuestion, isLoading: isBookmarkLoading } =
    useBookmarkQuestion(studentAssignmentId);
  const { mutate: endStudentAssignment, isLoading: isEndAssignmentLoading } =
    useEndStudentAssignment();

  const navigateToEndScreen = (reason) => {
    navigate(getAssignmentEndUrl({ studentAssignmentId, reason }));
  };

  const onEndTest = (endReason = ASSIGNMENT_END_REASON.STUDENT_ENDED) => {
    endStudentAssignment(
      { studentAssignmentId, endReason },
      { onSuccess: () => navigateToEndScreen(endReason) }
    );
  };

  // Security checks
  // Check if the assignment is already completed
  useContextEffect({
    deps: [assignmentData],
    ctx: { assignmentData, onEndTest },
    cb: (ctx) => {
      if (ctx.assignmentData && ctx.assignmentData?.status === STUDENTASSIGNMENT_STATUS.COMPLETED) {
        ctx.onEndTest(ASSIGNMENT_END_REASON.ALREADY_COMPLETED);
        return;
      }
    }
  });

  // Check if the user is allowed to take the assignment
  useCheckAssignmentAccess({ assignmentData });

  const {
    currentQuestion,
    currentQuestionIndex,
    currentQuestionResponse,
    nextQuestionId,
    previousQuestionId,
    isLastQuestion,
    isFirstQuestion
  } = useMemo(() => {
    try {
      return getAssignmentDetails({
        assignmentData,
        questionId
      });
    } catch (e) {
      console.error('Error getting assignment details: ', e);
      return {};
    }
  }, [assignmentData, questionId]);

  if (!assignmentData) {
    return {
      assignment: null
    };
  }

  const { assignment } = assignmentData;
  const navigateToNextQuestion = () => {
    if (nextQuestionId) {
      navigate(`/assignment/${studentAssignmentId}/${nextQuestionId}`);
    } else {
      console.info('Assignment end!!');
    }
  };

  const navigateToPreviousQuestion = () => {
    if (previousQuestionId) {
      navigate(`/assignment/${studentAssignmentId}/${previousQuestionId}`);
    }
  };

  const totalQuestions = assignmentData?.studentResponse?.length ?? 0;

  const onUpdateSuccess = () => {
    navigateToNextQuestion();
    refetchAssignment();
  };

  const markWriteConnectivityFailure = (err) => {
    if (!navigator.onLine) {
      dispatch(incrementFailureCount(API_IDS.SAVE_AND_CONTINUE));
      return;
    }
    const message = err?.message?.toLowerCase?.() ?? '';
    const code = err?.code;
    if (
      message.includes('timeout') ||
      message.includes('network') ||
      code === 'ERR_NETWORK' ||
      code === 'ECONNABORTED'
    ) {
      dispatch(incrementFailureCount(API_IDS.SAVE_AND_CONTINUE));
    }
  };

  const onSkip = (questionResponse) => {
    questionResponse.status = QUESTION_RESPONSE_STATUS.SKIPPED;
    questionResponse.answer = [];
    questionResponse.answers = {};
    delete questionResponse.uploadedFile;

    if (!navigator.onLine) {
      markWriteConnectivityFailure();
      return;
    }

    updateStudentAssignment(
      { data: questionResponse },
      { onSuccess: onUpdateSuccess, onError: markWriteConnectivityFailure }
    );
  };

  const onSave = async ({ uploadedFile, ...data }) => {
    const { questionResponse, answer, answers, type } = data;

    if (!navigator.onLine) {
      markWriteConnectivityFailure();
      return;
    }

    const onSuccess = () => {
      dispatch(resetAllFailureCounts());
      if (
        type === QUESTION_TYPES.FILE_UPLOAD ||
        type === QUESTION_TYPES.AUDIO ||
        type === QUESTION_TYPES.VIDEO
      ) {
        refetchAssignment();
      }
    };

    const onError = (err) => {
      markWriteConnectivityFailure(err);
    };

    switch (type) {
      case QUESTION_TYPES.FIB:
        delete questionResponse.uploadedFile;
        if (answers) questionResponse.answers = answers;
        break;
      case QUESTION_TYPES.FILE_UPLOAD:
        if (!hasAttemptedQuestion({ type, uploadedFile })) return;
        questionResponse.status = QUESTION_RESPONSE_STATUS.ANSWERED;
        try {
          await uploadStudentFileAsync(
            { data: questionResponse, uploadedFile },
            { onSuccess, onError }
          );
          questionResponse.uploadedFile = uploadedFile;
        } catch (error) {
          onError(error);
        }
        return;
      case QUESTION_TYPES.AUDIO:
      case QUESTION_TYPES.VIDEO:
        if (uploadedFile) {
          questionResponse.uploadedFile = uploadedFile;
          break;
        }
        // Retake: the API replaces the whole response and rejects `uploadedFile: null`,
        // so the key has to be omitted for the recording to actually be cleared.
        delete questionResponse.uploadedFile;
        questionResponse.status = QUESTION_RESPONSE_STATUS.NOT_VISITED;
        await updateStudentAssignmentAsync({ data: questionResponse }, { onSuccess, onError });
        return;
      case QUESTION_TYPES.MCQ:
      case QUESTION_TYPES.SUBJECTIVE:
      case QUESTION_TYPES.CODE:
      default:
        delete questionResponse.uploadedFile;
        if (typeof answer === 'string') {
          questionResponse.answer = [answer];
        } else if (Array.isArray(answer)) {
          questionResponse.answer = answer;
        }
        break;
    }

    if (
      !hasAttemptedQuestion({
        type,
        answer: questionResponse.answer,
        answers: questionResponse.answers,
        uploadedFile: uploadedFile || questionResponse.uploadedFile,
        question: currentQuestion
      })
    ) {
      return;
    }

    questionResponse.status = QUESTION_RESPONSE_STATUS.ANSWERED;
    await updateStudentAssignmentAsync(
      { data: questionResponse },
      {
        onSuccess,
        onError
      }
    );
  };

  const onSaveAndNext = async (data) => {
    const { shouldEndTest, type, uploadedFile, questionResponse, answer, answers } = data;

    if (
      hasAttemptedQuestion({
        type,
        answer: answer ?? questionResponse?.answer,
        answers: answers ?? questionResponse?.answers,
        uploadedFile: uploadedFile || questionResponse?.uploadedFile,
        question: currentQuestion
      })
    ) {
      await onSave(data);
    }

    if (shouldEndTest) {
      onEndTest(ASSIGNMENT_END_REASON.STUDENT_ENDED);
    } else {
      onUpdateSuccess();
    }
  };

  const bookmarkAction = (questionResponse) => {
    questionResponse.isBookmarked = !questionResponse.isBookmarked;
    bookmarkQuestion(questionResponse, { onSuccess: refetchAssignment });
  };

  const visitQuestion = (questionResponse) => {
    questionResponse.status = QUESTION_RESPONSE_STATUS.VISITED;
    updateStudentAssignment({ data: questionResponse }, { onSuccess: refetchAssignment });
  };

  return {
    assignment,
    isLastQuestion,
    isFirstQuestion,
    totalQuestions,
    currentQuestion,
    isBookmarkLoading,
    currentQuestionIndex,
    isEndAssignmentLoading,
    currentQuestionResponse,
    isSubmittingResponse: isUpdateLoading || isUploadLoading,
    onSkip,
    onSave,
    onNext: navigateToNextQuestion,
    onPrevious: navigateToPreviousQuestion,
    onEndTest,
    visitQuestion,
    onSaveAndNext,
    bookmarkAction
  };
};

export default useStudentAssignmentActions;
